<template>
  <div>
    <h3>Redis connection stat</h3>

    <template v-if="hasStat">
      <p class="mb-2">
        Total connection amount: {{totalConnectionAmount}}
      </p>
      <p class="mb-2">
        App — приложение, которое установило соединение к редису.<br>
        Source — хост, с которого установлено соединение к редису.<br>
        Amount — количество соединений к редису от конкретного приложения с конкретного хоста.
      </p>
      <table class="table w-auto text-nowrap">
        <thead class="thead-dark">
          <tr>
            <th scope="col">App</th>
            <th scope="col">Source</th>
            <th scope="col" class="text-right">Amount</th>
            <th scope="col" class="pr-1 text-right">Percent</th>
            <th></th>
          </tr>
        </thead>
        <tr
          v-for="group in stat"
          :key="createConnectionGroupKey(group)"
        >
          <td>{{group.name | getFormattedAppName}}</td>
          <td>{{group.host}}</td>
          <td class="text-right">{{group.amount}}</td>
          <td class="pr-1 text-right">{{getGroupPercentStr(group)}}</td>
          <td class="pl-0 align-middle">
            <b-progress
              :value="getGroupPercent(group)"
              :max="100"
              style="width: 150px"
            />
          </td>
        </tr>
      </table>
    </template>

    <loading-indicator
      v-if="isLoading"
    />

    <p v-if="message !== null">
      {{message}}
    </p>
  </div>
</template>

<script>
import { HTTP } from '../http-common';
import notifier from '../notifier';

import LoadingIndicator from './LoadingIndicator.vue';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

const UNKNOWN_APP_NAME = 'unknown';

export default {
  name: 'SystemRedisConnectionStat',

  components: {
    LoadingIndicator,
  },

  data() {
    return {
      loadState: LOAD_STATE_INIT,

      /** @type {Object[] | null} */
      stat: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {boolean} */
    hasStat() {
      return this.isLoadedNormal && this.stat !== null && this.stat.length > 0;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal && this.stat !== null && this.stat.length === 0) {
        return 'No connections.';
      }
      return null;
    },

    /** @returns {?number} */
    totalConnectionAmount() {
      const stat = this.stat;
      if(stat === null) {
        return null;
      }

      let total = 0;
      for(const group of stat) {
        total += group.amount;
      }
      return total;
    },
  },

  created() {
    this.loadStat();
  },

  methods: {
    loadStat() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      HTTP.get('/redis_connection_stat')
      .then(response => {
        const groups = response.data.data;
        groups.sort(compareGroups);

        this.stat = groups;
        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    createConnectionGroupKey(group) {
      return group.name.toString() + '_' + group.host.toString();
    },

    getGroupPercent(group) {
      const amount = group.amount;

      const total = this.totalConnectionAmount;
      if(total === null) {
        return null;
      }

      return amount / total * 100;
    },

    getGroupPercentStr(group) {
      const percent = this.getGroupPercent(group);

      const percentRounded = Math.round(percent);
      return percentRounded.toString() + '%';
    },
  },

  filters: {
    getFormattedAppName,
  },
};

function compareGroups(groupA, groupB) {
  if(groupA === groupB) {
    return 0;
  }

  const appAName = getFormattedAppName(groupA.name);
  const appBName = getFormattedAppName(groupB.name);

  if(appAName < appBName) {
    return -1;
  }
  if(appAName > appBName) {
    return 1;
  }

  const hostA = groupA.host;
  const hostB = groupB.host;
  if(hostA < hostB) {
    return -1;
  }
  if(hostA > hostB) {
    return 1;
  }

  return 0;
}

/**
 * @param {string} appName
 * @returns {string}
 */
function getFormattedAppName(appName) {
  if((appName === null) || (appName === undefined)) {
    return UNKNOWN_APP_NAME;
  }

  if(appName.length === 0) {
    return UNKNOWN_APP_NAME;
  }

  return appName;
}
</script>
